import { useState, useEffect } from 'react';
import { SlideMockup } from './PresentationSlideMockup';
import { SLIDE_PREVIEWS, THEME_DEFINITIONS } from './PresentationBannerConstants';

export interface LightboxTheme {
  name: string;
  preview: string;
  palette: string[];
}

interface Props {
  theme: LightboxTheme | null;
  onClose: () => void;
  onApply: () => void;
}

export function ThemeLightbox({ theme, onClose, onApply }: Props) {
  const [mounted, setMounted]           = useState(false);
  const [animIn, setAnimIn]             = useState(false);
  const [selectedSlide, setSelectedSlide] = useState<number | null>(null);

  useEffect(() => {
    if (theme) {
      setMounted(true);
      setSelectedSlide(null);
      const t = setTimeout(() => setAnimIn(true), 20);
      return () => clearTimeout(t);
    } else {
      setAnimIn(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [theme]);

  useEffect(() => {
    if (!theme) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { selectedSlide !== null ? setSelectedSlide(null) : onClose(); }
      if (selectedSlide !== null) {
        if (e.key === 'ArrowLeft')  setSelectedSlide(s => s !== null ? (s - 1 + 12) % 12 : null);
        if (e.key === 'ArrowRight') setSelectedSlide(s => s !== null ? (s + 1) % 12 : null);
      }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [theme, onClose, selectedSlide]);

  if (!mounted || !theme) return null;

  const themeDef = THEME_DEFINITIONS[theme.name] ?? THEME_DEFINITIONS['Midnight Pro'];

  return (
    <div className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-300 ${animIn ? 'opacity-100' : 'opacity-0'}`}>
      <div className="absolute inset-0 bg-black/88 backdrop-blur-lg" onClick={() => selectedSlide !== null ? setSelectedSlide(null) : onClose()} />

      <div className={`relative w-full max-w-5xl max-h-[90vh] flex flex-col rounded-3xl overflow-hidden border border-white/12 bg-slate-950 shadow-2xl transition-all duration-300 ${animIn ? 'scale-100 translate-y-0' : 'scale-95 translate-y-5'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0 bg-white/3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl overflow-hidden border border-white/20 flex-shrink-0">
              <img src={theme.preview} alt={theme.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-white">{theme.name} — Full Theme Preview</h3>
              <p className="text-xs text-slate-500">All 12 slide types rendered in this theme&apos;s colors</p>
            </div>
            <div className="flex gap-1.5 ml-2">
              {theme.palette.map(c => <div key={c} className="w-5 h-5 rounded-full border-2 border-white/20" style={{ backgroundColor: c }} />)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onApply} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white cursor-pointer hover:opacity-90 transition-all whitespace-nowrap" style={{ backgroundColor: themeDef.primaryColor }}>
              <i className="ri-check-line text-xs"></i>Apply Theme
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer transition-colors">
              <i className="ri-close-line text-slate-400"></i>
            </button>
          </div>
        </div>

        {/* Single slide fullview */}
        {selectedSlide !== null && (
          <div className="absolute z-10 flex flex-col bg-slate-950" style={{ inset: 0, top: '65px' }}>
            <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedSlide(null)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-arrow-left-line text-xs"></i>All slides
                </button>
                <div className="w-px h-4 bg-white/15" />
                <span className="text-xs font-bold text-white">{SLIDE_PREVIEWS[selectedSlide].type}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setSelectedSlide(s => s !== null ? (s - 1 + 12) % 12 : null)} className="w-7 h-7 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center cursor-pointer transition-colors">
                  <i className="ri-arrow-left-s-line text-slate-300 text-sm"></i>
                </button>
                <span className="text-xs text-slate-500 px-1">{selectedSlide + 1}/12</span>
                <button onClick={() => setSelectedSlide(s => s !== null ? (s + 1) % 12 : null)} className="w-7 h-7 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center cursor-pointer transition-colors">
                  <i className="ri-arrow-right-s-line text-slate-300 text-sm"></i>
                </button>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="w-full max-w-2xl aspect-video rounded-2xl overflow-hidden border border-white/15" style={{ backgroundColor: themeDef.bgColor }}>
                <SlideMockup slideIndex={selectedSlide} theme={themeDef} />
              </div>
            </div>
            <div className="px-6 pb-4 flex items-center justify-center gap-1.5">
              {SLIDE_PREVIEWS.map((_, i) => (
                <button key={i} onClick={() => setSelectedSlide(i)} className="rounded-full transition-all cursor-pointer" style={{ width: i === selectedSlide ? '16px' : '6px', height: '6px', backgroundColor: i === selectedSlide ? themeDef.primaryColor : 'rgba(255,255,255,0.2)' }} />
              ))}
            </div>
          </div>
        )}

        {/* 4×3 grid */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin' }}>
          <div className="grid grid-cols-4 gap-4">
            {SLIDE_PREVIEWS.map((slide, idx) => (
              <button key={slide.seq} onClick={() => setSelectedSlide(idx)} className="group flex flex-col gap-2 cursor-pointer text-left">
                <div className="aspect-video rounded-xl overflow-hidden border-2 border-transparent group-hover:border-white/35 transition-all duration-200 group-hover:scale-[1.03]" style={{ backgroundColor: themeDef.bgColor }}>
                  <SlideMockup slideIndex={idx} theme={themeDef} />
                </div>
                <div className="flex items-center justify-between px-0.5">
                  <span className="text-xs font-semibold text-slate-400 group-hover:text-white transition-colors truncate">{slide.type}</span>
                  <span className="text-xs text-slate-700 flex-shrink-0 ml-1">{idx + 1}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-white/10 flex-shrink-0 bg-white/2">
          <p className="text-xs text-slate-600">Click any slide to view full-size · Esc to close</p>
          <p className="text-xs text-slate-500">← → arrow keys to navigate in full view</p>
        </div>
      </div>
    </div>
  );
}
